import React, { useMemo, useCallback, useState } from 'react';
import { MerchantFields } from './MerchantFields';
import { IMerchant, FormField, FormGroups } from '~/Models/DTO/Merchant.models';
import config from '../HelperMerchant/merchantConfig.json';
import {
  checkDependencyByName,
  useMerchantValidations,
  mapFieldsIsRequired,
} from '../HelperMerchant/useMerchantValidations';
import { connect, FormikContextType } from 'formik';
import { useNavigation, useProvider, ToastVariant, useToastMessage, useLanguage } from '~/Hooks';
import { Route } from '~/Constants/Routes.constants';
import {
  requestUpdateMerchant,
  requestCreateMerchant,
  requestSetMerchantDefaultRate,
} from '~/requests/providerService/useRequestMerchants';
import { useQueryClient } from 'react-query';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { isEmpty } from 'lodash-es';
import { useTranslation } from 'react-i18next';
interface IUseFormGenerator {
  merchant: IMerchant;
  merchantId?: string;
  rateOptions: any[];
}

interface IMerchantFieldsrops {
  groups: FormGroups[];
  fields: FormField[];
  create?: boolean;
  errors?: any;
}

const MerchantFormikWrapper = connect<IMerchantFieldsrops, IMerchant>(
  ({
    groups,
    fields,
    formik,
    create = false,
  }: IMerchantFieldsrops & {
    formik: FormikContextType<IMerchant>;
  }) => {
    const setCustomField = useCallback(
      (ev: any) => formik.setFieldValue('customField', ev?.target?.value),
      [formik],
    );

    const setConsent = useCallback(() => {
      formik.setFieldValue('consent', !formik.values?.consent);
    }, [formik]);

    const setBillable = useCallback(() => {
      formik.setFieldValue('billable', !formik.values?.billable);
    }, [formik]);

    return (
      <MerchantFields
        groups={groups}
        fields={fields}
        setConsent={setConsent}
        setBillable={setBillable}
        consent={formik.values?.consent}
        billable={formik.values?.billable}
        customField={formik.values?.customField}
        country={formik.values?.country}
        setCustomField={setCustomField}
        create={create}
      />
    );
  },
);

export const useMerchantFormGenerator = ({
  merchant,
  merchantId = 'new',
  rateOptions,
}: IUseFormGenerator) => {
  const { values, asyncValidate, errors } = useMerchantValidations(merchant);
  const { showToast } = useToastMessage();
  const [isLoading, setIsLoading] = useState(false);
  const { navigate } = useNavigation();
  const queryClient = useQueryClient();
  const { currentProvider, getCurrentProvider } = useProvider();
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  const fields = useMemo(
    () =>
      mapFieldsIsRequired(
        values,
        config?.fields.map((field) => ({
          ...field,
          options: field.name === 'defaultSinglePayoutRate' ? rateOptions : field.options,
          type:
            field.name === 'defaultSinglePayoutRate' && merchantId !== 'new'
              ? 'select'
              : field.type,
        })),
      )?.filter(({ name }: FormField) => !checkDependencyByName(name, values)),
    [
      config?.fields,
      values?.country,
      values?.entities,
      rateOptions,
      merchant?.defaultSinglePayoutRate,
    ],
  );
  const groups = useMemo(() => config?.groups as FormGroups[], [config?.groups]);

  const sections = useMemo(
    () => [
      () => <MerchantFormikWrapper groups={groups} fields={fields} create={merchantId === 'new'} />,
    ],
    [fields, groups, currentLanguage],
  );
  const onSave = async () => {
    let errors = await asyncValidate(values);
    if (Object.keys(errors || {}).length) {
      return errors;
    }
    setIsLoading(true);

    try {
      if (merchantId === 'new') {
        if (!currentProvider?.organisationVenueId) {
          showToast(
            `${currentProvider?.uuid}`,
            t('app.organization.merchant.create.error.noVenueId'),
            ToastVariant.ERROR,
            true,
          );
          getCurrentProvider(currentProvider?.domain || '');
          setIsLoading(false);
          return;
        }
        await requestCreateMerchant({ ...values, venueId: currentProvider?.organisationVenueId });
      } else {
        if (typeof values?.defaultSinglePayoutRate === 'string' && values.country === 'nl') {
          await requestSetMerchantDefaultRate(merchant.uuid, values.defaultSinglePayoutRate);
        }
        await requestUpdateMerchant({
          ...values,
          venueId: currentProvider?.organisationVenueId,
          defaultSinglePayoutRate: undefined,
        });
      }
      setIsLoading(false);
      await queryClient?.invalidateQueries({ queryKey: [REQUEST_ACTIONS.GET_MERCHANT_LIST] });
    } catch (e: any) {
      const message = e?.detail;
      setIsLoading(false);
      if (!isEmpty(e)) {
        showToast(
          message || JSON.stringify(e),
          message || JSON.stringify(e),
          ToastVariant.ERROR,
          true,
        );
        return e;
      }
    }

    navigate(Route.ORGANISATION_MERCHANT, { domain: currentProvider?.domain || '' });

    return errors;
  };

  return {
    sections,
    validate: asyncValidate,
    onSave,
    hasErrors: !!Object.keys(errors || {}).length,
    isLoading,
  };
};
